/**
 * usePopularSections — секции popular_category страницы /bots/<category>.
 *
 * ОБЩИЙ модульный кэш: все потребители страницы (пеблы категорий, сетка карточек, блоки лент) делят ОДИН
 * запрос — кэшируется сырой ответ адреса, а «только popular_category» / «все секции» — фильтр у каждого
 * своего. Раньше `all` входил в ключ кэша, и /bots/trends качал одни и те же данные дважды (замер 30.09:
 * 50 КБ ×2 у гостя, 240 КБ ×2 у вошедшего). Ключ включает пользователя: бэк гостю вырезает готовые
 * промпты (prompt_locked), поэтому логин/логаут даёт свежий ключ → рефетч.
 *
 * Ранний старт: index.html запускает этот же запрос вместе с HTML (window.__umnikEarlySections), а не после
 * разбора бандла — на медленном 4G данные приходили на 12-й секунде, после скрипта в 882 КБ. Ответ, пришедший
 * до первого рендера, отдаётся СИНХРОННО: страница рисуется сразу с лентами, без заглушек.
 */
import { useEffect, useState } from 'react'

import api from '../../../api/client'
import { useAuth } from '../../../context/AuthContext'
import type { ToolPage } from '../utils/cardSeo.ts'

/** Категория инструментов (config.tool_categories секции, админ /settings/main). */
export interface ToolCategory {
	slug: string
	label: string
}

/** Минимальная форма секции — потребители уточняют своим типом (generic). */
export interface PopularSectionBase {
	id: string
	section_type: string
	sort_order?: number
	config: {
		title?: string
		/** Категории инструментов ПО СТРАНИЦАМ: { image: [...], video: [...] } —
		 *  у каждой страницы свой набор пеблов (админ /settings/main). */
		tool_categories?: Record<string, ToolCategory[]>
		[key: string]: unknown
	}
}

// Слаги, занятые каталожными разделами сайта (/bots/<page>/<slug>) — категория
// инструментов с таким слагом не может стать пеблом (админка их не создаёт,
// фронт страхуется на случай legacy-данных).
const RESERVED_TOOL_SLUGS = new Set(['trends', 'styles', 'all'])

type SectionsResponse = { ok: boolean; sections: PopularSectionBase[] }

/** Ранний запрос из index.html («Ранние данные страницы»). authed — ушёл ли он с токеном: ответ гостю и вошедшему разный. */
interface EarlySections {
	url: string
	authed: boolean
	data: SectionsResponse | null
	done: boolean
	promise: Promise<SectionsResponse | null>
}

declare global {
	interface Window {
		__umnikEarlySections?: EarlySections
	}
}

const sectionsOf = (d: SectionsResponse | null | undefined): PopularSectionBase[] | null =>
	d && d.ok && Array.isArray(d.sections) ? d.sections : null

/** Потребителю «только карточки» — секции popular_category, «всё» — ответ как есть. */
const pickSections = (secs: PopularSectionBase[], all: boolean): PopularSectionBase[] =>
	all ? secs : secs.filter((s) => s.section_type === 'popular_category')

/** Ранний ответ, если он для этого адреса и этого состояния входа. Забираем один раз. */
function takeEarly(url: string, authed: boolean): EarlySections | null {
	if (typeof window === 'undefined') return null
	const early = window.__umnikEarlySections
	if (!early || early.url !== url || early.authed !== authed) return null
	delete window.__umnikEarlySections
	return early
}

// Сырые ответы по адресу+пользователю: промис (идёт/пришёл) и уже пришедшие секции (для синхронного рендера).
const rawCache = new Map<string, Promise<PopularSectionBase[]>>()
const rawReady = new Map<string, PopularSectionBase[]>()

function loadRaw(rawKey: string, url: string, authed: boolean): Promise<PopularSectionBase[]> {
	const hit = rawCache.get(rawKey)
	if (hit) return hit
	const viaApi = () => api.request<SectionsResponse>(url).then((d) => sectionsOf(d) ?? [])
	const early = takeEarly(url, authed)
	const ready = early ? sectionsOf(early.data) : null
	if (ready) rawReady.set(rawKey, ready)
	const p = (ready
		? Promise.resolve(ready)
		: early
			// Ранний запрос не удался (сеть, протухший токен) — обычный путь с обновлением входа.
			? early.promise.then((d) => sectionsOf(d) ?? viaApi())
			: viaApi())
		.then((secs) => {
			rawReady.set(rawKey, secs)
			return secs
		})
		.catch(() => {
			// Ошибку не кэшируем — следующий маунт попробует снова.
			rawCache.delete(rawKey)
			return [] as PopularSectionBase[]
		})
	rawCache.set(rawKey, p)
	return p
}

export function usePopularSections<T extends PopularSectionBase = PopularSectionBase>(
	category: ToolPage,
	opts?: {
		/** true — вернуть ВСЕ секции страницы (в т.ч. trend_feed для блоков
		 *  «главной трендов»), не только popular_category. Тот же запрос, свой фильтр. */
		all?: boolean
		/** true — стафф-превью (?preview=1): бэк отдаёт и скрытые/вне
		 *  расписания секции (не-стаффу бэк просто игнорирует флаг). */
		preview?: boolean
	},
): { sections: T[] | null; loading: boolean } {
	const all = !!opts?.all
	const preview = !!opts?.preview
	const { user } = useAuth()
	const authed = !!user
	const url = `/api/v1/admin/page-sections/public/page/home?page=${category}${preview ? '&preview=1' : ''}`
	const rawKey = `${category}|${user?.id ?? 'guest'}${preview ? '|prev' : ''}`
	const key = `${rawKey}${all ? '|all' : ''}`
	// Stale-key паттерн (без синхронного setState в эффекте): ответ применяем
	// только к актуальному ключу, смена ключа сама «обнуляет» derived-значение.
	const [state, setState] = useState<{ key: string; secs: T[] } | null>(null)

	useEffect(() => {
		let cancelled = false
		void loadRaw(rawKey, url, authed).then((secs) => {
			if (!cancelled) setState({ key, secs: pickSections(secs, all) as T[] })
		})
		return () => {
			cancelled = true
		}
	}, [key, rawKey, url, authed, all])

	// Ответ уже пришёл (ранний запрос из index.html или прошлый маунт) — отдаём сразу, в первом же рендере.
	const ready = state && state.key === key ? null : rawReady.get(rawKey) ?? (() => {
		const early = typeof window !== 'undefined' ? window.__umnikEarlySections : undefined
		return early && early.done && early.url === url && early.authed === authed ? sectionsOf(early.data) : null
	})()
	const sections = state && state.key === key ? state.secs : ready ? (pickSections(ready, all) as T[]) : null
	return { sections, loading: sections === null }
}

/** Категории инструментов КОНКРЕТНОЙ страницы: из всех её секций, уникальные
 *  по слагу, в порядке появления; зарезервированные слаги отфильтрованы. */
export function toolCategoriesOf(
	sections: PopularSectionBase[] | null,
	page: ToolPage,
): ToolCategory[] {
	if (!sections) return []
	const seen = new Set<string>()
	const out: ToolCategory[] = []
	for (const s of sections) {
		const byPage = s.config.tool_categories
		const list = byPage && !Array.isArray(byPage) ? byPage[page] : undefined
		for (const tc of list ?? []) {
			if (!tc?.slug || seen.has(tc.slug) || RESERVED_TOOL_SLUGS.has(tc.slug)) continue
			seen.add(tc.slug)
			out.push({ slug: tc.slug, label: tc.label || tc.slug })
		}
	}
	return out
}
